/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * useWorkflowEvents — React hook that orchestrates a workflow run.
 *
 * Owns the run snapshot, the stage state machine, and the event log.
 * Opens the SSE stream (workflowEventsClient) iff the run is not in a
 * terminal state, then folds events into:
 *
 *   - run: latest snapshot ({ state, outputs, artifacts, current_stage_id, ...})
 *   - stages: array of { id, name, archetype, persona, status, tools,
 *                       latency_ms, llm_tier, started_at, completed_at,
 *                       awaiting_confirmation, confirmation_id }
 *   - events: reverse-chronological raw log entries
 *   - pendingConfirmation: { stage_id, tool_id, tool_inputs, confirmation_id }
 *                          | null  (consumed by ApprovalModal in Slice 4)
 *   - connection: 'snapshot' | 'live' | 'reconnecting' | 'closed' | 'error'
 *
 * Event vocabulary handled (defensive — unknown events still feed the
 * log so the demo never silently drops):
 *
 *   stage.started, stage.completed, stage.failed,
 *   stage.awaiting_confirmation
 *   persona.loaded
 *   context.gathering, context.observed
 *   reason.starting
 *   tool.invoked, tool.completed, tool.confirmation_requested
 *   act.generating, response.token
 *   memory.committed
 *   run.completed, run.failed, session.idle
 *   error
 *
 * Reconciliation: when we get a snapshot that has stages, we use that
 * as the source of truth. SSE events update the same stage objects in
 * place. If a stage_id arrives that the snapshot didn't list, we
 * append it (defensive against V2 emitting new stages mid-run).
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import workflowAdminApi, {
  extractRun,
  extractStages,
} from '../services/workflowAdminApi';
import { openRunEventStream } from '../services/workflowEventsClient';

const TERMINAL_STATES = new Set([
  'completed', 'failed', 'cancelled', 'rejected',
]);

const MAX_EVENTS = 500;
const MAX_TOKEN_BUFFER = 50000;

// ─────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────

const initial = {
  run: null,
  stages: [],
  events: [],
  pendingConfirmation: null,
  connection: 'snapshot',
  error: null,
  tokenBuffer: '', // accumulated response.token stream from the act stage
};

function ensureStage(stages, stageId, patch = {}) {
  if (!stageId) return stages;
  const idx = stages.findIndex((s) => s.id === stageId);
  if (idx === -1) {
    return [...stages, { id: stageId, name: stageId, status: 'running', tools: [], ...patch }];
  }
  const next = [...stages];
  next[idx] = { ...next[idx], ...patch };
  return next;
}

function updateStageTools(stages, stageId, updater) {
  if (!stageId) return stages;
  const idx = stages.findIndex((s) => s.id === stageId);
  if (idx === -1) {
    return [...stages, {
      id: stageId, name: stageId, status: 'running',
      tools: updater([]),
    }];
  }
  const next = [...stages];
  next[idx] = { ...next[idx], tools: updater(next[idx].tools || []) };
  return next;
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SNAPSHOT': {
      const { run, stages } = action;
      return {
        ...state,
        run,
        stages: stages.length > 0 ? stages : state.stages,
        connection: TERMINAL_STATES.has(String(run?.state).toLowerCase())
          ? 'closed'
          : state.connection,
      };
    }
    case 'CONNECTION': return { ...state, connection: action.connection };
    case 'ERROR': return { ...state, error: action.error };
    case 'CLEAR_PENDING': return { ...state, pendingConfirmation: null };
    case 'APPEND_EVENT': {
      const events = [action.entry, ...state.events];
      if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;
      return { ...state, events };
    }
    case 'STAGE_PATCH':
      return { ...state, stages: ensureStage(state.stages, action.stageId, action.patch) };
    case 'STAGE_TOOLS':
      return { ...state, stages: updateStageTools(state.stages, action.stageId, action.updater) };
    case 'PENDING_CONFIRM':
      return { ...state, pendingConfirmation: action.payload };
    case 'TOKEN':
      return {
        ...state,
        tokenBuffer: (state.tokenBuffer + (action.token || '')).slice(-MAX_TOKEN_BUFFER),
      };
    case 'RUN_UPDATE':
      return { ...state, run: { ...(state.run || {}), ...action.patch } };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────

export default function useWorkflowEvents(runId) {
  const [state, dispatch] = useReducer(reducer, initial);
  const streamRef = useRef(null);

  const append = useCallback((eventName, data) => {
    dispatch({
      type: 'APPEND_EVENT',
      entry: {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        at: new Date().toISOString(),
        event: eventName,
        data,
        stageId: data?.stage_id || null,
        severity: eventName === 'error' ? 'error'
          : eventName.endsWith('.failed') ? 'error'
          : eventName === 'run.completed' ? 'success'
          : 'info',
      },
    });
  }, []);

  const dispatchEvent = useCallback((eventName, data) => {
    append(eventName, data);
    const stageId = data?.stage_id;

    switch (eventName) {
      case 'stage.started':
        dispatch({ type: 'STAGE_PATCH', stageId, patch: {
          status: 'running',
          name: data?.name || stageId,
          archetype: data?.archetype || data?.archetype_id,
          started_at: data?.at || new Date().toISOString(),
        }});
        dispatch({ type: 'RUN_UPDATE', patch: { current_stage_id: stageId } });
        break;
      case 'stage.completed':
        dispatch({ type: 'STAGE_PATCH', stageId, patch: {
          status: 'completed',
          completed_at: data?.at || new Date().toISOString(),
          latency_ms: data?.latency_ms,
          llm_tier: data?.llm_tier || data?.tier,
        }});
        break;
      case 'stage.failed':
        dispatch({ type: 'STAGE_PATCH', stageId, patch: {
          status: 'failed',
          error: data?.error || data?.message,
          completed_at: data?.at || new Date().toISOString(),
        }});
        break;
      case 'stage.awaiting_confirmation':
        dispatch({ type: 'STAGE_PATCH', stageId, patch: {
          status: 'awaiting_confirmation',
          awaiting_confirmation: true,
          confirmation_id: data?.confirmation_id,
        }});
        break;

      case 'persona.loaded':
        dispatch({ type: 'STAGE_PATCH', stageId: stageId || state.run?.current_stage_id, patch: {
          persona: data?.display_name || data?.name || data?.persona_id,
          persona_id: data?.persona_id || data?.id,
        }});
        break;

      case 'tool.invoked':
        dispatch({ type: 'STAGE_TOOLS', stageId, updater: (tools) => {
          const name = data?.tool_name || data?.tool_id || 'tool';
          return [...tools, { name, status: 'running', invoked_at: new Date().toISOString() }];
        }});
        break;

      case 'tool.completed':
        dispatch({ type: 'STAGE_TOOLS', stageId, updater: (tools) => {
          const name = data?.tool_name || data?.tool_id;
          const idx = tools.findIndex((t) => t.name === name && t.status === 'running');
          if (idx === -1) return [...tools, { name, status: 'completed', latency_ms: data?.latency_ms || data?.duration_ms }];
          const next = [...tools];
          next[idx] = { ...next[idx], status: 'completed', latency_ms: data?.latency_ms || data?.duration_ms };
          return next;
        }});
        break;

      case 'tool.confirmation_requested':
        dispatch({ type: 'PENDING_CONFIRM', payload: {
          stage_id: stageId,
          tool_id: data?.tool_id || data?.tool_name,
          tool_inputs: data?.tool_inputs || data?.inputs,
          tool_description: data?.tool_description || data?.description,
          confirmation_id: data?.confirmation_id,
          persona: data?.persona,
        }});
        dispatch({ type: 'STAGE_PATCH', stageId, patch: {
          status: 'awaiting_confirmation',
          awaiting_confirmation: true,
          confirmation_id: data?.confirmation_id,
        }});
        break;

      case 'response.token':
        if (data?.token) dispatch({ type: 'TOKEN', token: data.token });
        break;

      case 'run.completed':
        dispatch({ type: 'RUN_UPDATE', patch: {
          state: 'completed',
          outcome: data?.outcome || data?.outputs,
          artifacts: data?.artifacts,
          total_latency_ms: data?.total_latency_ms,
          completed_at: data?.at || new Date().toISOString(),
        }});
        dispatch({ type: 'CONNECTION', connection: 'closed' });
        break;

      case 'run.failed':
        dispatch({ type: 'RUN_UPDATE', patch: {
          state: 'failed',
          error: data?.error || data?.message,
          completed_at: data?.at || new Date().toISOString(),
        }});
        dispatch({ type: 'CONNECTION', connection: 'closed' });
        break;

      default:
        // Unknown event — already in the log via append(), no state mutation.
        break;
    }
  }, [append, state.run]);

  // ─────────────────────────────────────────────────────────────────
  // Lifecycle: 1) snapshot 2) open stream if non-terminal 3) clean up
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;

    (async () => {
      let snapshotState = null;
      try {
        const body = await workflowAdminApi.getRun(runId);
        const run = extractRun(body);
        const stages = extractStages(body);
        if (cancelled) return;
        if (!run) {
          dispatch({ type: 'ERROR', error: { kind: 'not_found', message: 'Run not found.' } });
          dispatch({ type: 'CONNECTION', connection: 'closed' });
          return;
        }
        dispatch({ type: 'SET_SNAPSHOT', run, stages });
        snapshotState = String(run.state || '').toLowerCase();
      } catch (err) {
        const status = err?.response?.status;
        dispatch({ type: 'ERROR', error: {
          kind: status === 404 ? 'not_found'
            : status === 403 ? 'permission'
            : status === 401 ? 'auth'
            : 'server',
          message: err?.response?.data?.error || err?.message || 'Failed to load run.',
        }});
        dispatch({ type: 'CONNECTION', connection: 'error' });
        return;
      }

      if (TERMINAL_STATES.has(snapshotState)) {
        dispatch({ type: 'CONNECTION', connection: 'closed' });
        return;
      }

      // Open the live stream.
      streamRef.current = openRunEventStream(runId, {
        onOpen: () => dispatch({ type: 'CONNECTION', connection: 'live' }),
        onEvent: dispatchEvent,
        onError: (e) => {
          // Non-fatal — surface but let the reconnect loop continue if it wants.
          append('error', e);
        },
        onReconnecting: (attempt, delay) => {
          dispatch({ type: 'CONNECTION', connection: 'reconnecting' });
          append('connection.reconnecting', { attempt, delay_ms: delay });
        },
        onEnd: () => dispatch({ type: 'CONNECTION', connection: 'closed' }),
      });
    })();

    return () => {
      cancelled = true;
      streamRef.current?.close?.();
      streamRef.current = null;
    };
  // dispatchEvent + append are stable enough (useCallback) but they
  // close over state.run — we accept the re-create on run change because
  // the stream is closed/reopened only when runId changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // ─────────────────────────────────────────────────────────────────
  // Imperative API for the page
  // ─────────────────────────────────────────────────────────────────

  const clearPendingConfirmation = useCallback(() => {
    dispatch({ type: 'CLEAR_PENDING' });
  }, []);

  const refreshSnapshot = useCallback(async () => {
    try {
      const body = await workflowAdminApi.getRun(runId);
      const run = extractRun(body);
      const stages = extractStages(body);
      if (run) dispatch({ type: 'SET_SNAPSHOT', run, stages });
    } catch (_) { /* ignore — stream is authoritative */ }
  }, [runId]);

  return {
    run: state.run,
    stages: state.stages,
    events: state.events,
    pendingConfirmation: state.pendingConfirmation,
    connection: state.connection,
    error: state.error,
    tokenBuffer: state.tokenBuffer,
    clearPendingConfirmation,
    refreshSnapshot,
  };
}
